import { ForcedState } from '../../../constants';
import { WrappedWithRepromise } from '../../common/repromise';
import { CombinedCollectionSettings } from '../../types';

interface LoginOptions {
  recaptchaToken?: string;
  invisibleRecaptchaToken?: string;
}

interface PromptLoginOptions {
  mode?: 'login' | 'signup';
  modal?: boolean;
  // Relayed when switching between login and signup so the reopened popup keeps the
  // post-login-redirect flag (thunderbolt reads it from the promptLogin options).
  allowPostAuthRedirect?: boolean;
}

export type PromptAuthPageCallback = <T extends TpaPage>(
  page: T,
  param?: TpaPageParams[T],
) => Promise<any>;

export type LoginCallback = (
  email: string,
  password: string,
  options: LoginOptions,
) => Promise<any>;

export type PromptLoginCallback = (
  loginOptions?: PromptLoginOptions,
  isCloseable?: boolean,
  returnPages?: boolean,
) => Promise<any>;

export enum TpaPage {
  EXPIRED_RESET_LINK = 'EXPIRED_RESET_LINK',
  ADMIN_APPROVAL = 'ADMIN_APPROVAL',
  NO_PERMISSIONS = 'NO_PERMISSIONS',
  LOGIN = 'LOGIN',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  RESET_PASSWORD = 'RESET_PASSWORD',
  EMAIL_CONFIRMATION = 'EMAIL_CONFIRMATION',
}

export type TpaPageParams = {
  [TpaPage.EXPIRED_RESET_LINK]: undefined;
  [TpaPage.ADMIN_APPROVAL]: string;
  [TpaPage.NO_PERMISSIONS]: (() => void) | undefined;
  [TpaPage.LOGIN]: LoginOptions | undefined;
  [TpaPage.FORGOT_PASSWORD]:
    | boolean
    | { isCloseable?: boolean; allowPostAuthRedirect?: boolean }
    | undefined;
  [TpaPage.RESET_PASSWORD]: undefined;
  [TpaPage.EMAIL_CONFIRMATION]: undefined;
};

export type ControllerProps = {
  // All functions passed as props must be wrapped with repromise
  // They must be unwrapped with useRepromise before normal use
  login: WrappedWithRepromise<LoginCallback>;
  loginWithIdp: WrappedWithRepromise<
    (idpConnectionId: string, joinCommunity?: boolean) => Promise<unknown>
  >;
  promptAuthPage: WrappedWithRepromise<PromptAuthPageCallback>;
  promptLogin: WrappedWithRepromise<PromptLoginCallback>;
  isEditor: boolean;
  isEditor3: boolean;
  isStudio2: boolean;
  isPreview?: boolean;
  forceView: ForcedState;
  settings: CombinedCollectionSettings;
  closeLightbox: WrappedWithRepromise<() => void>;
  // Whether this flow allows a post-auth redirect (set by thunderbolt).
  allowPostAuthRedirect?: boolean;
};
